import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';

import LoginPage from './LoginPage';
import Layout from './Layout';
import MapPage from './MapPage';
import AISDataPage from './AISDataPage';
import AnalyticsPage from './AnalyticsPage';
import AdvancedAnalyticsPage from './AdvancedAnalyticsPage';
import PerformanceNewReportPage from './PerformanceNewReportPage';
import EnginePerformanceNewReportPage from './EnginePerformanceNewReportPage';
import HullCleaningNewReportPage from './HullCleaningNewReportPage';
import PerformanceRecordsPage from './PerformanceRecordsPage';
import EngineRecordsPage from './EngineRecordsPage';
import CleaningRecordsPage from './CleaningRecordsPage';
import AdminUsersPage from './AdminUsersPage';
import AdminShipsPage from './AdminShipsPage';
import AdminCompanyPage from './AdminCompanyPage';
import OperationalPage from './OperationalPage';
import PerformanceTemplate from './PerformanceTemplate';
import PositionPage from './PositionPage';
import WeatherPage from './WeatherPage';
import TechnicalPage from './TechnicalPage';
import EnginePage from './EnginePage';
import ROBPage from './ROBPage';
import CalculatedPage from './CalculatedPage';
import EnginePerformanceTemplate from './EnginePerformanceTemplate';
import TemplateMapPage from './TemplateMapPage';
import DatabaseShipsPage from './DatabaseShipsPage';
import DatabaseUsersPage from './DatabaseUsersPage';
import DatabaseCompaniesPage from './DatabaseCompaniesPage';

function App() {
  // auth only in memory
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userType, setUserType]           = useState('');

  // helpers
  const isSuperAdmin   = userType === 'Super Admin';
  const canSeeAdvanced = ['Super Admin','Admin','Company Admin'].includes(userType);

  return (
    <Router>
      <Routes>
        {/* PUBLIC */}
        <Route
          path="/"
          element={
            !isAuthenticated
              ? <LoginPage
                  setIsAuthenticated={setIsAuthenticated}
                  setUserType={setUserType}
                />
              : <Navigate to="/app" replace/>
          }
        />
        <Route
          path="/login"
          element={
            <LoginPage
              setIsAuthenticated={setIsAuthenticated}
              setUserType={setUserType}
            />
          }
        />

        {/* OPTIONAL ALIAS */}
        <Route path="/map" element={<Navigate to="/app/map" replace/>}/>

        {/* PROTECTED */}
        <Route
          path="/app/*"
          element={
            isAuthenticated
              ? <Layout
                  userType={userType}
                  setIsAuthenticated={setIsAuthenticated}
                />
              : <Navigate to="/login" replace/>
          }
        >
          {/* CORE */}
          <Route index element={<MapPage/>}/>
          <Route path="map" element={<MapPage/>}/>

          {/* AIS DATA (restricted) */}
          <Route
            path="ais-data"
            element={
              canSeeAdvanced
                ? <AISDataPage/>
                : <Navigate to="/app" replace/>
            }
          />

          {/* VESSEL ANALYTICS */}
          <Route path="analytics" element={<AnalyticsPage/>}/>

          {/* ADVANCED ANALYTICS (restricted) */}
          <Route
            path="advanced-analytics"
            element={
              canSeeAdvanced
                ? <AdvancedAnalyticsPage/>
                : <Navigate to="/app" replace/>
            }
          />

          {/* PERFORMANCE */}
          <Route path="performance">
            <Route path="new-report" element={<PerformanceNewReportPage/>}/>
            <Route path="records"    element={<PerformanceRecordsPage/>}/>
          </Route>

          {/* ENGINE PERFORMANCE */}
          <Route path="engine-performance">
            <Route path="new-report" element={<EnginePerformanceNewReportPage/>}/>
            <Route path="records"    element={<EngineRecordsPage/>}/>
          </Route>

          {/* HULL CLEANING */}
          <Route path="hull-cleaning">
            <Route path="new-report" element={<HullCleaningNewReportPage/>}/>
            <Route path="records"    element={<CleaningRecordsPage/>}/>
          </Route>

          {/* ADMIN (Super Admin only) */}
          {isSuperAdmin && (
            <Route path="admin">
              <Route path="users"   element={<AdminUsersPage/>}/>
              <Route path="ships"   element={<AdminShipsPage/>}/>
              <Route path="company" element={<AdminCompanyPage/>}/>

              {/* TEMPLATES */}
              <Route path="templates">
                <Route path="operational"        element={<OperationalPage/>}/>
                <Route path="performance"        element={<PerformanceTemplate/>}/>
                <Route path="position"           element={<PositionPage/>}/>
                <Route path="weather"            element={<WeatherPage/>}/>
                <Route path="technical"          element={<TechnicalPage/>}/>
                <Route path="engine"             element={<EnginePage/>}/>
                <Route path="rob"                element={<ROBPage/>}/>
                <Route path="calculated"         element={<CalculatedPage/>}/>
                <Route path="engine-performance" element={<EnginePerformanceTemplate/>}/>
                <Route path="template-map"       element={<TemplateMapPage/>}/>
              </Route>

              {/* DATABASES */}
              <Route path="databases">
                <Route path="ships"     element={<DatabaseShipsPage/>}/>
                <Route path="users"     element={<DatabaseUsersPage/>}/>
                <Route path="companies" element={<DatabaseCompaniesPage/>}/>
              </Route>
            </Route>
          )}
        </Route>

        {/* CATCH-ALL */}
        <Route
          path="*"
          element={<Navigate to={isAuthenticated ? "/app" : "/login"} replace/>}
        />
      </Routes>
    </Router>
  );
}

export default App;
